import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Mail, Loader2, Check, Copy, Key, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CredentialsRecoveryProps {
  onClose?: () => void;
}

export default function CredentialsRecovery({ onClose }: CredentialsRecoveryProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState<{
    name?: string;
    email?: string;
    password?: string;
  } | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: 'Error',
        description: 'Por favor, introduce tu dirección de correo electrónico.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Intentar recuperar las credenciales desde la tabla de perfiles
      const { data, error } = await supabase
        .from('profiles')
        .select('name, email, temp_password')
        .eq('email', email)
        .single();

      if (error || !data) {
        throw new Error('No se encontraron credenciales para este correo electrónico.');
      }

      // Mostrar las credenciales al usuario
      setCredentials({
        name: data.name,
        email: data.email,
        password: data.temp_password
      });

      toast({
        title: 'Credenciales recuperadas',
        description: 'Tus datos de acceso han sido encontrados.',
        variant: 'default',
      });
    } catch (error: any) {
      console.error('Error al recuperar credenciales:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron recuperar las credenciales. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copiado',
      description: `${label} copiado al portapapeles.`,
      variant: 'default',
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Recuperar credenciales</h3>
        <p className="text-zinc-400 text-sm">
          Introduce tu correo electrónico para recuperar tus datos de acceso
        </p>
      </div>

      {!credentials ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="email" className="text-white/90 flex items-center gap-2 text-sm font-medium">
              <Mail className="w-4 h-4 text-[#FCAA0D]" />
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
              placeholder="ejemplo@correo.com"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2 mt-4"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Buscando...
              </>
            ) : (
              'Recuperar credenciales'
            )}
          </button>
        </form>
      ) : (
        <div className="mt-6">
          <div className="bg-zinc-800/80 border border-zinc-700 rounded-lg p-4 mb-4">
            <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Tus credenciales de acceso
            </h4>

            <div className="space-y-3">
              {credentials.name && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#FCAA0D]" />
                    <span className="text-zinc-400">Nombre:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white">{credentials.name}</span>
                    <button
                      onClick={() => credentials.name && copyToClipboard(credentials.name, 'Nombre')}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#FCAA0D]" />
                  <span className="text-zinc-400">Email:</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white">{credentials.email}</span>
                  <button
                    onClick={() => credentials.email && copyToClipboard(credentials.email, 'Email')}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {credentials.password && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-[#FCAA0D]" />
                    <span className="text-zinc-400">Contraseña:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white">{credentials.password}</span>
                    <button
                      onClick={() => credentials.password && copyToClipboard(credentials.password, 'Contraseña')}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-zinc-400 text-sm mt-4">
            Recuerda guardar estas credenciales en un lugar seguro. Las necesitarás para acceder a tu cuenta.
          </p>

          <button
            onClick={onClose}
            className="w-full bg-zinc-700 text-white font-medium px-6 py-3 rounded-xl hover:bg-zinc-600 transition-colors mt-4"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
